"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { EventCarousel } from "./EventCarousel";
import { EventShowcaseClient } from "./EventShowcaseClient";
import {
  LANDING_PAGE_SIZE,
  loadLandingEvents,
  type LandingErrorCode,
  type LandingEventsResult,
} from "@/data/eventsLanding";
import { type EventsFilterState, DEFAULT_EVENTS_FILTERS } from "@/utils/eventsFilters";

const landingErrorMessageKey: Record<LandingErrorCode, string> = {
  featured: "landing.errors.featured",
  grid: "landing.errors.grid",
  unexpected: "landing.errors.unexpected",
};

/**
 * Client landing shell that loads public event carousels/grids after hydration.
 */
export function EventsLanding({ filters = DEFAULT_EVENTS_FILTERS }: { filters?: EventsFilterState }) {
  const t = useTranslations("events");
  const [data, setData] = useState<LandingEventsResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    void loadLandingEvents(filters).then((result) => {
      if (!cancelled) setData(result);
    });
    return () => {
      cancelled = true;
    };
  }, [filters]);

  if (!data) {
    return (
      <div className="gruv-events">
        <div className="gruv-skeleton gruv-skeleton--carousel" />
        <div className="gruv-skeleton gruv-skeleton--grid" />
      </div>
    );
  }

  if (!data.ok) {
    return (
      <section className="gruv-error">
        <h2>{t("landing.errorTitle")}</h2>
        <p>{t(landingErrorMessageKey[data.errorCode])}</p>
      </section>
    );
  }

  return (
    <div className="gruv-events">
      <EventCarousel events={data.popular} />
      <EventShowcaseClient
        featured={data.featured}
        spotlight={data.spotlight}
        initialPage={3}
        pageSize={LANDING_PAGE_SIZE}
        totalPages={data.totalPages}
        initialFilters={filters}
      />
    </div>
  );
}
